///
/// EarthBrowser
///
/// Unified command list for the Layers panel search: quick actions,
/// per-layer visibility toggles, and feature-registry browsers that have
/// no layer row of their own (dev tools). Browsers already represented as
/// layer rows are excluded to avoid duplicate search results.
///
/// Created by Matt Giger on 9/11/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useCommandRegistry } from "@/hooks/useCommandRegistry"
import { setOnboardingOpen } from "@/redux/dialogSlice"
import { sendCameraCommand, sendDatasetAction, setTourSettings } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { saveCurrentView } from "@/utils/savedViews"
import { BookmarkPlus, GraduationCap, Orbit, Timer } from "lucide-react"
import { useMemo } from "react"
import type { CommandItem } from "./commandTypes"
import { LAYERS } from "./layerRegistry"

export function useUnifiedCommands(): CommandItem[] {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const autoOrbit = useAppSelector(state => state.earthbrowser.tourSettings.autoOrbit)
	const { commands: registryCommands } = useCommandRegistry()

	return useMemo(() => {
		// Browsers that already appear as tappable layer rows
		const layerCommandIds = new Set(LAYERS.map(layer => layer.commandId).filter(Boolean))

		return [
			{
				id: 'save-view',
				type: 'action' as const,
				title: 'Save Current View',
				subtitle: 'Bookmark this camera position (⌘D)',
				icon: <BookmarkPlus className="w-4 h-4 text-white/70" />,
				keywords: ['save', 'view', 'bookmark', 'camera', 'position'],
				action: () => {
					void saveCurrentView()
				},
				enabled: true
			},
			{
				id: 'toggle-orbit',
				type: 'action' as const,
				title: 'Toggle Camera Orbit',
				subtitle: 'Slowly circle the current view',
				icon: <Orbit className="w-4 h-4 text-white/70" />,
				keywords: ['orbit', 'rotate', 'spin', 'camera', 'cinematic', 'motion'],
				action: () => {
					dispatch(sendCameraCommand({ command: "orbit/toggle" }))
				},
				enabled: true
			},
			{
				id: 'toggle-auto-orbit',
				type: 'action' as const,
				title: autoOrbit ? 'Disable Auto-Orbit When Idle' : 'Enable Auto-Orbit When Idle',
				subtitle: 'Start orbiting after 45 seconds of inactivity',
				icon: <Timer className="w-4 h-4 text-white/70" />,
				keywords: ['auto', 'orbit', 'idle', 'screensaver', 'attract', 'camera'],
				action: () => {
					dispatch(setTourSettings({ autoOrbit: !autoOrbit }))
				},
				enabled: true
			},
			{
				id: 'show-tutorial',
				type: 'action' as const,
				title: 'Show Tutorial',
				subtitle: 'Replay the getting started guide',
				icon: <GraduationCap className="w-4 h-4 text-white/70" />,
				keywords: ['tutorial', 'onboarding', 'help', 'guide', 'tour', 'getting started'],
				action: () => {
					dispatch(setOnboardingOpen(true))
				},
				enabled: true
			},
			// Per-layer visibility toggles (search-only; browse mode has switches)
			...LAYERS.filter(layer => layer.visibility).map(layer => {
				const visible = visibleDatasets.includes(layer.visibility!)
				return {
					id: `toggle-layer-${layer.id}`,
					type: 'action' as const,
					title: visible ? `Hide ${layer.name}` : `Show ${layer.name}`,
					subtitle: 'Layers',
					icon: layer.icon,
					keywords: ['toggle', 'layer', 'show', 'hide', layer.name.toLowerCase()],
					action: () => {
						dispatch(sendDatasetAction({
							type: layer.visibility!,
							action: visible ? 'disable' : 'enable'
						}))
					},
					enabled: true
				}
			}),
			// Registry browsers without a layer row (SQL terminal, storage manager, ...)
			...registryCommands
				.filter(cmd => !layerCommandIds.has(cmd.id))
				.map(cmd => ({
					id: cmd.id,
					type: 'tool' as const,
					title: cmd.title,
					subtitle: cmd.category,
					icon: cmd.icon,
					keywords: [cmd.title.toLowerCase()],
					action: cmd.onSelect,
					enabled: cmd.enabled ?? true
				}))
		]
	}, [dispatch, registryCommands, visibleDatasets, autoOrbit])
}
